import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, FormControl, FormLabel, Input, Select, Textarea,
  SimpleGrid, VStack, HStack, Text, Badge, useToast, Tabs, TabList, Tab,
  TabPanels, TabPanel, Table, Thead, Tbody, Tr, Th, Td, Checkbox, IconButton,
  Box, Divider, Link,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { MdAdd, MdUpload, MdCheckCircle, MdLink } from 'react-icons/md';
import type { AudienciaJuzgado } from './juzgadoTypes';
import juzgadoApi from '../../services/juzgadoApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  audiencia: AudienciaJuzgado | null;
}

const ESTATUS_ACTA = [
  { value: 'EN_REDACCION', label: 'En redacción', color: 'orange' },
  { value: 'LISTA_REVISION', label: 'Lista para revisión', color: 'blue' },
  { value: 'LISTA_FIRMA', label: 'Lista para firma', color: 'purple' },
  { value: 'FIRMADA', label: 'Firmada', color: 'green' },
];

export default function ModalDesarrolloAudiencia({ isOpen, onClose, audiencia }: Props) {
  const toast = useToast();
  const fileRefId = useRef<HTMLInputElement>(null);
  const fileRefDoc = useRef<HTMLInputElement>(null);
  const [checkinPendiente, setCheckinPendiente] = useState<number | null>(null);

  // Acta
  const [contenidoActa, setContenidoActa] = useState('');
  const [estatusActa, setEstatusActa] = useState('EN_REDACCION');
  const [audioUrl, setAudioUrl] = useState('');
  const [escribiente, setEscribiente] = useState('');
  const [savingActa, setSavingActa] = useState(false);

  // Check-ins
  const [checkins, setCheckins] = useState<any[]>([]);
  const [nuevoCheckin, setNuevoCheckin] = useState({ nombreParticipante: '', rol: 'Imputado' });

  // Documentos presentados
  const [docs, setDocs] = useState<any[]>([]);
  const [nuevoDoc, setNuevoDoc] = useState({ tipo: 'ESCRITO_SISTEMA', nombre: '', contenidoTexto: '', presentadoPor: '' });

  const cargar = async () => {
    if (!audiencia?.id) return;
    try {
      const [acta, cks, dcs] = await Promise.all([
        juzgadoApi.obtenerActa(audiencia.id),
        juzgadoApi.listarCheckIns(audiencia.id),
        juzgadoApi.listarDocsPresentados(audiencia.id),
      ]);
      if (acta) {
        setContenidoActa(acta.contenidoHtml ?? '');
        setEstatusActa(acta.estatusActa ?? 'EN_REDACCION');
        setAudioUrl(acta.audioUrl ?? '');
        setEscribiente(acta.escribiente ?? (audiencia.escribiente ?? ''));
      } else {
        setContenidoActa('');
        setEstatusActa('EN_REDACCION');
        setAudioUrl('');
        setEscribiente(audiencia.escribiente ?? '');
      }
      setCheckins(cks);
      setDocs(dcs);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    }
  };

  useEffect(() => { if (isOpen) cargar(); }, [isOpen, audiencia]);

  const guardarActa = async () => {
    if (!audiencia) return;
    setSavingActa(true);
    try {
      await juzgadoApi.guardarActa(audiencia.id, {
        contenidoHtml: contenidoActa,
        estatusActa,
        audioUrl: audioUrl || null,
        escribiente,
      });
      toast({ title: 'Acta guardada', status: 'success' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSavingActa(false);
    }
  };

  const agregarCheckin = async () => {
    if (!audiencia || !nuevoCheckin.nombreParticipante) return toast({ title: 'Nombre requerido', status: 'warning' });
    try {
      await juzgadoApi.crearCheckIn(audiencia.id, nuevoCheckin);
      setNuevoCheckin({ nombreParticipante: '', rol: 'Imputado' });
      await cargar();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    }
  };

  const subirIne = async (checkinId: number, file: File) => {
    try {
      await juzgadoApi.subirIdentificacionCheckIn(checkinId, file);
      toast({ title: 'Identificación verificada', status: 'success' });
      await cargar();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    }
  };

  const agregarDoc = async (file?: File) => {
    if (!audiencia || !nuevoDoc.nombre) return toast({ title: 'Nombre del documento requerido', status: 'warning' });
    try {
      await juzgadoApi.crearDocPresentado(audiencia.id, nuevoDoc, file);
      setNuevoDoc({ tipo: 'ESCRITO_SISTEMA', nombre: '', contenidoTexto: '', presentadoPor: '' });
      await cargar();
      toast({ title: 'Documento registrado', status: 'success' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    }
  };

  const actaColor = ESTATUS_ACTA.find(e => e.value === estatusActa)?.color ?? 'gray';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          Desarrollo de Audiencia — {audiencia?.folioAudiencia}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody pt={4}>
          <Tabs colorScheme="red" variant="enclosed">
            <TabList>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Check-in</Tab>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Documentos</Tab>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Acta de Audiencia</Tab>
            </TabList>
            <TabPanels>

              {/* ── CHECK-IN ──────────────────────────────────────────── */}
              <TabPanel px={0}>
                <Box p={3} border="1px solid" borderColor="#bb8c52" borderRadius="md" mb={3}>
                  <Text fontWeight="semibold" color="#5b182b" mb={2}>Registrar participante</Text>
                  <HStack>
                    <FormControl>
                      <FormLabel fontSize="sm">Nombre</FormLabel>
                      <Input size="sm" value={nuevoCheckin.nombreParticipante} onChange={e => setNuevoCheckin(p => ({ ...p, nombreParticipante: e.target.value }))} borderColor="#bb8c52" />
                    </FormControl>
                    <FormControl maxW="200px">
                      <FormLabel fontSize="sm">Rol</FormLabel>
                      <Select size="sm" value={nuevoCheckin.rol} onChange={e => setNuevoCheckin(p => ({ ...p, rol: e.target.value }))} borderColor="#bb8c52">
                        {['Imputado','Víctima','Testigo','Representante legal','Juez','Secretario','Custodia'].map(r => <option key={r} value={r}>{r}</option>)}
                      </Select>
                    </FormControl>
                    <Button mt={6} size="sm" leftIcon={<MdAdd />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={agregarCheckin}>
                      Check-in
                    </Button>
                  </HStack>
                </Box>

                <input type="file" hidden ref={fileRefId} accept="image/*,application/pdf"
                  onChange={e => { const f = e.target.files?.[0]; if (f && checkinPendiente) subirIne(checkinPendiente, f); e.target.value = ''; }} />

                <Table size="sm">
                  <Thead bg="gray.100">
                    <Tr><Th>Participante</Th><Th>Rol</Th><Th>Hora</Th><Th>Identidad</Th><Th>Acción</Th></Tr>
                  </Thead>
                  <Tbody>
                    {checkins.length === 0 && <Tr><Td colSpan={5} textAlign="center" color="gray.400">Sin participantes registrados</Td></Tr>}
                    {checkins.map(c => (
                      <Tr key={c.id}>
                        <Td>{c.nombreParticipante}</Td>
                        <Td><Badge>{c.rol}</Badge></Td>
                        <Td fontSize="xs">{new Date(c.horaCheckIn).toLocaleTimeString('es-MX')}</Td>
                        <Td>
                          {c.identidadVerificada
                            ? <Badge colorScheme="green"><MdCheckCircle style={{ display: 'inline' }} /> Verificada</Badge>
                            : <Badge colorScheme="gray">Pendiente</Badge>}
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <IconButton aria-label="Subir INE" icon={<MdUpload />} size="xs" colorScheme="blue" variant="outline"
                              onClick={() => { setCheckinPendiente(c.id); fileRefId.current?.click(); }} />
                            {c.identificacionUrl && <Link href={c.identificacionUrl} isExternal><IconButton aria-label="Ver" icon={<MdLink />} size="xs" variant="ghost" /></Link>}
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TabPanel>

              {/* ── DOCUMENTOS PRESENTADOS ────────────────────────────── */}
              <TabPanel px={0}>
                <Box p={3} border="1px solid" borderColor="#bb8c52" borderRadius="md" mb={3}>
                  <Text fontWeight="semibold" color="#5b182b" mb={2}>Presentar documento</Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} mb={2}>
                    <FormControl>
                      <FormLabel fontSize="sm">Tipo</FormLabel>
                      <Select size="sm" value={nuevoDoc.tipo} onChange={e => setNuevoDoc(p => ({ ...p, tipo: e.target.value }))} borderColor="#bb8c52">
                        <option value="ESCRITO_SISTEMA">Escrito en sistema</option>
                        <option value="PDF">PDF / archivo</option>
                        <option value="IMAGEN">Imagen / escaneo</option>
                        <option value="EVIDENCIA">Evidencia digital</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Nombre</FormLabel>
                      <Input size="sm" value={nuevoDoc.nombre} onChange={e => setNuevoDoc(p => ({ ...p, nombre: e.target.value }))} borderColor="#bb8c52" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Presentado por</FormLabel>
                      <Input size="sm" value={nuevoDoc.presentadoPor} onChange={e => setNuevoDoc(p => ({ ...p, presentadoPor: e.target.value }))} borderColor="#bb8c52" />
                    </FormControl>
                  </SimpleGrid>
                  {nuevoDoc.tipo === 'ESCRITO_SISTEMA' ? (
                    <>
                      <FormControl mb={2}>
                        <FormLabel fontSize="sm">Contenido del escrito</FormLabel>
                        <Textarea size="sm" rows={3} value={nuevoDoc.contenidoTexto} onChange={e => setNuevoDoc(p => ({ ...p, contenidoTexto: e.target.value }))} borderColor="#bb8c52" />
                      </FormControl>
                      <Button size="sm" leftIcon={<MdAdd />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={() => agregarDoc()}>Registrar escrito</Button>
                    </>
                  ) : (
                    <>
                      <input type="file" hidden ref={fileRefDoc} accept="image/*,application/pdf"
                        onChange={e => { const f = e.target.files?.[0]; if (f) agregarDoc(f); e.target.value = ''; }} />
                      <Button size="sm" leftIcon={<MdUpload />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={() => fileRefDoc.current?.click()}>Subir archivo</Button>
                    </>
                  )}
                </Box>

                <Table size="sm">
                  <Thead bg="gray.100">
                    <Tr><Th>Tipo</Th><Th>Nombre</Th><Th>Presentado por</Th><Th>Fecha</Th><Th>Archivo</Th></Tr>
                  </Thead>
                  <Tbody>
                    {docs.length === 0 && <Tr><Td colSpan={5} textAlign="center" color="gray.400">Sin documentos presentados</Td></Tr>}
                    {docs.map(d => (
                      <Tr key={d.id}>
                        <Td><Badge>{d.tipo}</Badge></Td>
                        <Td>{d.nombre}</Td>
                        <Td>{d.presentadoPor ?? '—'}</Td>
                        <Td fontSize="xs">{new Date(d.createdAt).toLocaleDateString('es-MX')}</Td>
                        <Td>{d.url ? <Link href={d.url} isExternal color="#5b182b">Ver</Link> : '—'}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TabPanel>

              {/* ── ACTA DE AUDIENCIA ─────────────────────────────────── */}
              <TabPanel px={0}>
                <HStack mb={3} justify="space-between">
                  <HStack>
                    <Text fontWeight="semibold" color="#5b182b">Estatus del acta:</Text>
                    <Badge colorScheme={actaColor}>{ESTATUS_ACTA.find(e => e.value === estatusActa)?.label}</Badge>
                  </HStack>
                  <Select maxW="220px" size="sm" value={estatusActa} onChange={e => setEstatusActa(e.target.value)} borderColor="#bb8c52">
                    {ESTATUS_ACTA.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </Select>
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Escribiente</FormLabel>
                    <Input size="sm" value={escribiente} onChange={e => setEscribiente(e.target.value)} borderColor="#bb8c52" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Link de grabación de audio</FormLabel>
                    <Input size="sm" value={audioUrl} onChange={e => setAudioUrl(e.target.value)} placeholder="URL del archivo de audio" borderColor="#bb8c52" />
                  </FormControl>
                </SimpleGrid>
                <FormControl>
                  <FormLabel fontSize="sm">Redacción del acta (editable en tiempo real)</FormLabel>
                  <Textarea
                    value={contenidoActa}
                    onChange={e => setContenidoActa(e.target.value)}
                    rows={14}
                    borderColor="#bb8c52"
                    _focus={{ borderColor: '#5b182b' }}
                    placeholder="Escriba aquí el desarrollo de la audiencia, intervenciones, acuerdos y resolución..."
                    isReadOnly={estatusActa === 'FIRMADA'}
                  />
                </FormControl>
                <HStack justify="flex-end" mt={3}>
                  <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} isLoading={savingActa} onClick={guardarActa} isDisabled={estatusActa === 'FIRMADA' && false}>
                    Guardar Acta
                  </Button>
                </HStack>
              </TabPanel>

            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
